import { useState } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      setFormMessage('Grazie per l\'iscrizione!');
      return;
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d9b34jlv8hat0f6uqim0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email: formData.get('email') as string }).toString(),
      });
      const responseText = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('Grazie per l\'iscrizione! Riceverai aggiornamenti SEO.');
        setEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg && serverMsg.includes('spam')) {
          setFormStatus('error');
          setFormMessage('Si è verificato un errore. Riprova più tardi.');
        } else {
          setFormStatus('error');
          setFormMessage(serverMsg || 'Errore durante l\'iscrizione. Riprova.');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('Errore di connessione. Riprova più tardi.');
    }
  };

  return (
    <footer className="bg-secondary-50 pt-16 md:pt-20 pb-8 px-4 md:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
          {/* Left: Brand + Newsletter */}
          <div className="lg:w-2/5">
            <p className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 leading-tight mb-1">
              Itaobuy
            </p>
            <p className="text-xl md:text-2xl font-heading font-light text-foreground-400 mb-4">
              Spreadsheet
            </p>
            <p className="text-sm text-foreground-500 leading-relaxed mb-8 max-w-sm">
              Piattaforma indipendente di ricerca e analisi prodotti. Non siamo affiliati direttamente con i venditori — offriamo informazioni trasparenti e verificate.
            </p>

            {/* Newsletter */}
            <form
              data-readdy-form
              id="newsletter-footer"
              onSubmit={handleNewsletterSubmit}
              className="max-w-sm"
            >
              <label htmlFor="footer-email" className="text-xs font-label text-foreground-600 mb-2 block">
                Ricevi aggiornamenti SEO
              </label>
              <div className="flex gap-2">
                <input
                  id="footer-email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="la tua email"
                  className="flex-1 bg-transparent border-b border-foreground-300 px-1 py-2 text-sm text-foreground-800 outline-none focus:border-foreground-600 transition-colors placeholder:text-foreground-400"
                />
                <button
                  type="submit"
                  className="px-5 py-2 rounded-full border border-foreground-300 text-foreground-600 text-xs font-label font-medium cursor-pointer whitespace-nowrap hover:bg-foreground-100 transition-colors"
                >
                  Iscriviti
                </button>
              </div>
              {/* Honeypot */}
              <input
                name="website_alt"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="absolute left-[-9999px] opacity-0 h-0 w-0 overflow-hidden pointer-events-none"
              />
              {formStatus !== 'idle' && (
                <p className={`text-xs mt-2 ${formStatus === 'success' ? 'text-accent-600' : 'text-red-500'}`}>
                  {formMessage}
                </p>
              )}
            </form>
          </div>

          {/* Right: Navigation Columns */}
          <div className="lg:w-3/5 grid grid-cols-2 md:grid-cols-3 gap-8">
            {/* Guide Hub */}
            <div>
              <h4 className="font-label font-semibold text-sm text-foreground-900 mb-4">Guide Hub</h4>
              <ul className="space-y-2.5">
                <li>
                  <Link to="/cos-e-itaobuy-spreadsheet" className="text-sm text-foreground-500 hover:text-foreground-800 hover:underline transition-colors">
                    Cos&apos;è Itaobuy Spreadsheet
                  </Link>
                </li>
                <li>
                  <Link to="/come-funziona-itaobuy" className="text-sm text-foreground-500 hover:text-foreground-800 hover:underline transition-colors">
                    Come Funziona
                  </Link>
                </li>
                <li>
                  <Link to="/itaobuy-sicuro" className="text-sm text-foreground-500 hover:text-foreground-800 hover:underline transition-colors">
                    Sicurezza
                  </Link>
                </li>
                <li>
                  <Link to="/migliori-prodotti-itaobuy" className="text-sm text-foreground-500 hover:text-foreground-800 hover:underline transition-colors">
                    Migliori Prodotti
                  </Link>
                </li>
              </ul>
            </div>

            {/* Categorie */}
            <div>
              <h4 className="font-label font-semibold text-sm text-foreground-900 mb-4">Categorie</h4>
              <ul className="space-y-2.5">
                {categories.slice(0, 7).map((cat) => (
                  <li key={cat.id}>
                    <a
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-sm text-foreground-500 hover:text-foreground-800 hover:underline transition-colors"
                    >
                      {cat.nameIt}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Trust & Info */}
            <div>
              <h4 className="font-label font-semibold text-sm text-foreground-900 mb-4">Trust &amp; Info</h4>
              <ul className="space-y-2.5">
                <li>
                  <Link to="/cos-e-itaobuy-spreadsheet" className="text-sm text-foreground-500 hover:text-foreground-800 hover:underline transition-colors">
                    Team Editoriale
                  </Link>
                </li>
                <li>
                  <Link to="/itaobuy-sicuro" className="text-sm text-foreground-500 hover:text-foreground-800 hover:underline transition-colors">
                    Centro Sicurezza
                  </Link>
                </li>
                <li>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-foreground-500 hover:text-foreground-800 hover:underline transition-colors"
                  >
                    Piattaforma Principale
                  </a>
                </li>
                <li>
                  <Link to="/" className="text-sm text-foreground-500 hover:text-foreground-800 hover:underline transition-colors">
                    Home
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-14 pt-6 border-t border-foreground-200/40 flex flex-col sm:flex-row justify-between items-center gap-3">
          <p className="text-xs text-foreground-400">
            &copy; 2026 itaobuyspread.cfd &mdash; Tutti i diritti riservati. Guida indipendente a Itaobuy Spreadsheet.
          </p>
          <p className="text-xs text-foreground-400">
            Ultimo aggiornamento: Luglio 2026 &bull; Reviewed by Itaobuy Research Team
          </p>
        </div>
      </div>
    </footer>
  );
}